import React from 'react';
import PropTypes from 'prop-types';
import {withStyles} from '@material-ui/core/styles/index'
import {reduxForm, Field} from 'redux-form';
import green from "@material-ui/core/colors/green";
import Pagination from './Pagination';
import TextField from "@material-ui/core/TextField";
import Typography from '@material-ui/core/Typography';
import FormGroup from "@material-ui/core/FormGroup";
import Button from "@material-ui/core/Button";
import CircularProgress from "@material-ui/core/CircularProgress";
import Paper from "@material-ui/core/Paper";
import {Avatar, List, ListItem} from "@material-ui/core";
import Highlighter from 'react-highlight-words';
import dateFormat from 'dateformat';

const styles = theme => ({
    root: {
        margin: theme.spacing.unit,
        padding: theme.spacing.unit,
    },
    pad: {
        padding: theme.spacing.unit,
    },
    actions: {
        display: 'flex',
        flexWrap: 'wrap',
    },
    container: {
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
    },
    formRow: {
        flex: 1,
        padding: theme.spacing.unit,
        paddingTop: 0,
        minWidth: 300,
    },
    buttonWrapper: {
        margin: theme.spacing.unit,
        position: 'relative',
    },
    buttonProgress: {
        color: green[500],
        position: 'absolute',
        top: '50%',
        left: '50%',
        marginTop: -12,
        marginLeft: -12,
    },
    pageButton: {
        minWidth: 0,
        marginRight: 4,
    },
    searchResult: {
        marginTop: theme.spacing.unit,
        marginBottom: theme.spacing.unit,
        padding: theme.spacing.unit,
        alignItems: 'flex-start',
    },
    avatar: {
        width: 80,
        height: 80,
        [theme.breakpoints.down('xs')]: {
            width: 40,
            height: 40,
        },
    },
    listItemText: {
        paddingLeft: theme.spacing.unit * 2,
    },
    title: {
        marginRight: theme.spacing.unit,
        [theme.breakpoints.down('xs')]: {
            fontSize: 18,
        },
    },
    distance: {
        fontSize: 14,
    },
    badge: {
        width: 36,
        height: 36,
    },
    small: {
        fontSize: 11,
    },
    highlighted: {
        backgroundColor: '#72b73a',
    },
    formError: {
        marginLeft: theme.spacing.unit,
    },
});

const renderTextField = ({
    input,
    meta,
    ...props,
}) => {
    const helperText = !!meta.error ? meta.error : props.helperText;
    return <TextField
        {...input}
        {...props}
        error={!!meta.error}
        helperText={helperText}
    />;
};

const Search = ({
    classes,
    count,
    results,
    error,
    handleSubmit,
    fetching,
    performCleanSubmit,
    performSubmitWithPage,
    formValues,
    submitSucceeded,
    limitExceeded,
}) => (
    <div>
        <Paper className={classes.root}>
            <div className={classes.pad}>
                <Typography variant="body2" color="default">
                    Finden sie eine Freie Pflegekraft in ihrer Nähe. Sie können in der Umkreis-Suche nach Ortsnamen, Postleitzahlen oder kompletten Anschriften suchen und zusätzlichen weitere Suchbegriffe angeben.
                </Typography>
            </div>
            <form onSubmit={handleSubmit(performCleanSubmit)}>
                <div className={classes.container}>
                    <FormGroup className={classes.formRow} key="location">
                        <Field
                            name="location"
                            label="Umkreis-Suche"
                            helperText="Ein Ortsname, PLZ oder komplette Anschrift"
                            component={renderTextField}
                        />
                    </FormGroup>
                    <FormGroup className={classes.formRow} key="searchTerm">
                        <Field
                            name="searchTerm"
                            label="Suchbegriff"
                            helperText="Eine Dienstleistung oder der Name eines Pflegers"
                            component={renderTextField}
                        />
                    </FormGroup>
                </div>
                {error && <Typography className={classes.formError} color="error" variant="body2">{error}</Typography>}
                <div className={classes.actions}>
                    <div className={classes.buttonWrapper} key="actions">
                        <Button disabled={fetching} type="submit" color="primary" variant="contained">
                            Jetzt finden!
                        </Button>
                        {fetching && <CircularProgress size={24} className={classes.buttonProgress}/>}
                    </div>
                </div>
            </form>
        </Paper>
        {(submitSucceeded && !limitExceeded && !fetching && !results.length) && <Typography color="error" variant="body2">Leider haben wir zu deiner Suchanfrage keine Pfleger gefunden.</Typography>}
        {(limitExceeded && !fetching) && <Typography color="error" variant="body2">
            Sie haben die maximale Anzahl Suchanfragen erreicht. Sie können ab {dateFormat(limitExceeded, 'germanDateTime')} weiter suchen.
        </Typography>}
        {0 < results.length && <div>
            <List ref={ref => this.listRef=ref}>
                {results.map((individual, index) => (
                    <ListItem className={classes.searchResult} key={index} button onClick={() => window.location.href= '/site/profile?id='+individual.id}>
                        <Avatar src={individual.imgSrc ? individual.imgSrc : '/images/no-image-found.png'} className={classes.avatar} />
                        <div className={classes.listItemText}>
                            <div className={classes.container}>
                                <Typography className={classes.title} variant="h6">
                                    <Highlighter
                                        highlightClassName={classes.highlighted}
                                        searchWords={formValues.searchTerm && formValues.searchTerm.length >= 3 ? [formValues.searchTerm] : []}
                                        autoEscape={true}
                                        textToHighlight={individual.firstname +' '+ individual.lastname}
                                    />
                                </Typography>
                                {individual.certificates && Object.keys(individual.certificates).map((key, index) => (
                                    <Avatar key={index} className={classes.badge} src={"/images/tiers/"+individual.certificates[key].icon} title={individual.certificates[key].name}/>
                                ))}
                                {!!individual.distance && <Typography className={classes.distance} variant="subtitle1">
                                    {(individual.distance / 1000).toFixed(2)} km ({individual.address.city ? individual.address.city.trimRight() : individual.address.state})
                                </Typography>}
                            </div>
                            <Typography variant="body1" className={classes.small}>
                                <Highlighter
                                    highlightClassName={classes.highlighted}
                                    searchWords={formValues.searchTerm && formValues.searchTerm.length >= 3 ? [formValues.searchTerm] : []}
                                    autoEscape={true}
                                    textToHighlight={individual.careServices.map(careService => careService.careService.name).join(', ')}
                                />
                            </Typography>
                        </div>
                    </ListItem>
                ))}
            </List>
            <Pagination
                disabled={fetching}
                current={formValues.page}
                defaultCurrent={1}
                total={count}
                defaultPageSize={30}
                pageSize={30}
                showLessItems
                showPrevNextJumpers={false}
                onChange={current => performSubmitWithPage(formValues, current).then(() => window.scrollTo(0, this.listRef.offsetTop))}
            />
        </div>}
    </div>
);

Search.propTypes = {
    classes: PropTypes.object.isRequired,
    fetching: PropTypes.bool.isRequired,
    performCleanSubmit: PropTypes.func.isRequired,
    performSubmitWithPage: PropTypes.func.isRequired,
    searchError: PropTypes.string,
    reset: PropTypes.func.isRequired,
    error: PropTypes.string,
    results: PropTypes.array.isRequired,
    count: PropTypes.number.isRequired,
    formValues: PropTypes.object,
    limitExceeded: PropTypes.string,
};

export default reduxForm({
    form: 'SearchForm',
})(withStyles(styles, {withTheme: true})(Search));


// WEBPACK FOOTER //
// ./src/components/Search.js